import React, { useCallback, useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowRight, Music, Brain, CircleDot, Leaf, Sparkles, Zap } from 'lucide-react';
import { useClickSound } from '../hooks/useClickSound';
interface AuraVisualizationProps {
  energyPoints: number;
  showSimPanel?: boolean;
  onOpenScanner?: () => void;
  onOpenSoundCatalog?: () => void;
  onOpenBeliefDetox?: () => void;
  onOpenChakraCleansing?: () => void;
  onOpenAffirmations?: () => void;
}
interface Particle {
  id: string;
  startX: number;
  startY: number;
  color: string;
}
interface OrbitingDot {
  id: string;
  size: number;
  distance: number;
  duration: number;
  direction: number;
}
function playSingingBowl() {
  try {
    const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
    const ctx = new AudioCtx();
    // Fundamental tone
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = 'sine';
    osc1.frequency.value = 256;
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    const now = ctx.currentTime;
    gain1.gain.setValueAtTime(0, now);
    gain1.gain.linearRampToValueAtTime(0.25, now + 0.3);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 4);
    osc1.start(now);
    osc1.stop(now + 4);
    // Harmonic overtone
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = 'sine';
    osc2.frequency.value = 384;
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    gain2.gain.setValueAtTime(0, now);
    gain2.gain.linearRampToValueAtTime(0.12, now + 0.2);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 3);
    osc2.start(now);
    osc2.stop(now + 3);
    // Shimmer overtone
    const osc3 = ctx.createOscillator();
    const gain3 = ctx.createGain();
    osc3.type = 'sine';
    osc3.frequency.value = 512;
    osc3.connect(gain3);
    gain3.connect(ctx.destination);
    gain3.gain.setValueAtTime(0, now);
    gain3.gain.linearRampToValueAtTime(0.06, now + 0.15);
    gain3.gain.exponentialRampToValueAtTime(0.001, now + 2.5);
    osc3.start(now);
    osc3.stop(now + 2.5);
  } catch (e) {
    // Audio not supported
  }
}
export function AuraVisualization({
  energyPoints,
  showSimPanel = false,
  onOpenScanner,
  onOpenSoundCatalog,
  onOpenBeliefDetox,
  onOpenChakraCleansing,
  onOpenAffirmations
}: AuraVisualizationProps) {
  const [fillStep, setFillStep] = useState(0);
  const [baseGlow, setBaseGlow] = useState(0);
  const [particles, setParticles] = useState<Particle[]>([]);
  const [orbitingDots, setOrbitingDots] = useState<OrbitingDot[]>([]);
  const [isPulsing, setIsPulsing] = useState(false);
  const [showAuraPopup, setShowAuraPopup] = useState(false);
  const orbRef = useRef<HTMLDivElement>(null);
  const {
    playClick,
    playSuccess
  } = useClickSound();
  // Derived state for visuals
  const getVisualState = () => {
    switch (fillStep) {
      case 0:
        return {
          ring1: 0,
          ring2: 0,
          ring3: 0,
          dirtyOpacity: 1,
          cleanOpacity: 0,
          status: 'Twoja energia jest uśpiona.',
          sub: 'Czas na przebudzenie',
          statusColor: 'text-slate-500',
          glow: baseGlow > 0 ? 'rgba(250, 204, 21, 0.4)' : 'rgba(0,0,0,0.05)',
          glowSize: baseGlow > 0 ? baseGlow : 30
        };
      case 1:
        return {
          ring1: 1,
          ring2: 0,
          ring3: 0,
          dirtyOpacity: 0.7,
          cleanOpacity: 0.3,
          status: 'Energia zaczyna płynąć.',
          sub: 'Kontynuuj praktykę',
          statusColor: 'text-emerald-600',
          glow: 'rgba(52, 211, 153, 0.4)',
          glowSize: 25 + baseGlow
        };
      case 2:
        return {
          ring1: 1,
          ring2: 1,
          ring3: 0,
          dirtyOpacity: 0.3,
          cleanOpacity: 0.7,
          status: 'Twoja wibracja wzrasta.',
          sub: 'Jesteś na dobrej drodze',
          statusColor: 'text-emerald-600',
          glow: 'rgba(14, 165, 233, 0.4)',
          glowSize: 45 + baseGlow
        };
      case 3:
      default:
        return {
          ring1: 1,
          ring2: 1,
          ring3: 1,
          dirtyOpacity: 0,
          cleanOpacity: 1,
          status: 'Twoja aura jest w pełni naładowana.',
          sub: 'Harmonia osiągnięta',
          statusColor: 'text-emerald-600',
          glow: 'rgba(99, 102, 241, 0.5)',
          glowSize: 75 + baseGlow
        };
    }
  };
  const visual = getVisualState();
  const handleFillRitual = (step: number, color: string, event: React.MouseEvent) => {
    if (step <= fillStep) return;
    playClick();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const startX = rect.left + rect.width / 2;
    const startY = rect.top + rect.height / 2;
    const newParticle: Particle = {
      id: Date.now().toString(),
      startX,
      startY,
      color
    };
    setParticles(prev => [...prev, newParticle]);
    if (navigator.vibrate) navigator.vibrate(50);
    // Remove particle and update state after animation
    setTimeout(() => {
      setParticles(prev => prev.filter(p => p.id !== newParticle.id));
      setFillStep(step);
      setIsPulsing(true);
      playSuccess();
      setTimeout(() => setIsPulsing(false), 400);
    }, 800);
  };
  const handleFillPoints = (event: React.MouseEvent) => {
    playClick();
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const startX = rect.left + rect.width / 2;
    const startY = rect.top + rect.height / 2;
    const newParticle: Particle = {
      id: Date.now().toString(),
      startX,
      startY,
      color: '#facc15'
    };
    setParticles(prev => [...prev, newParticle]);
    if (navigator.vibrate) navigator.vibrate([50, 50, 100]);
    setTimeout(() => {
      setParticles(prev => prev.filter(p => p.id !== newParticle.id));
      setBaseGlow(prev => prev + 15);
      setIsPulsing(true);
      // Add orbiting dot
      const newDot: OrbitingDot = {
        id: Date.now().toString(),
        size: 6 + Math.random() * 6,
        distance: 100 + Math.random() * 40,
        duration: 6 + Math.random() * 6,
        direction: Math.random() > 0.5 ? 1 : -1
      };
      setOrbitingDots(prev => [...prev, newDot]);
      setTimeout(() => setIsPulsing(false), 400);
    }, 800);
  };
  const handleReset = () => {
    playClick();
    setFillStep(0);
    setBaseGlow(0);
    setOrbitingDots([]);
    if (navigator.vibrate) navigator.vibrate(30);
  };
  return <div className="w-full flex flex-col items-center mb-6" data-id="element-69">
      {/* AURA DISPLAY */}
      <div className="flex-1 flex flex-col items-center justify-center min-h-[250px] relative mb-6 mt-4 w-full" data-id="element-70">
        {/* Main Aura Container */}
        <div className="relative flex items-center justify-center w-64 h-64" ref={orbRef} data-id="element-71">
          {/* Orbiting Dots */}
          <div className="absolute inset-0 z-30 pointer-events-none flex items-center justify-center" data-id="element-72">
            {orbitingDots.map(dot => <motion.div key={dot.id} className="absolute" animate={{
            rotate: dot.direction > 0 ? 360 : -360
          }} transition={{
            duration: dot.duration,
            repeat: Infinity,
            ease: 'linear'
          }} data-id="element-73">
                <div className="rounded-full bg-yellow-400 shadow-[0_0_10px_#facc15,0_0_20px_#eab308]" style={{
              width: dot.size,
              height: dot.size,
              transform: `translateX(${dot.distance}px) translateY(-50%)`
            }} data-id="element-74" />
              </motion.div>)}
          </div>

          {/* Energy Rings */}
          <motion.div className="absolute w-40 h-40 border-2 border-emerald-400/80 pointer-events-none z-10" style={{
          borderRadius: '40% 60% 70% 30% / 40% 50% 60% 50%'
        }} animate={{
          rotate: 360,
          opacity: visual.ring1
        }} transition={{
          rotate: {
            duration: 12,
            repeat: Infinity,
            ease: 'linear'
          },
          opacity: {
            duration: 1
          }
        }} data-id="element-75" />
          <motion.div className="absolute w-48 h-48 border-2 border-cyan-400/70 pointer-events-none z-10" style={{
          borderRadius: '60% 40% 30% 70% / 50% 60% 50% 40%'
        }} animate={{
          rotate: -360,
          opacity: visual.ring2
        }} transition={{
          rotate: {
            duration: 18,
            repeat: Infinity,
            ease: 'linear'
          },
          opacity: {
            duration: 1
          }
        }} data-id="element-76" />
          <motion.div className="absolute w-56 h-56 border-2 border-indigo-400/60 pointer-events-none z-10" style={{
          borderRadius: '50% 50% 60% 40% / 60% 40% 50% 50%'
        }} animate={{
          rotate: 360,
          opacity: visual.ring3
        }} transition={{
          rotate: {
            duration: 25,
            repeat: Infinity,
            ease: 'linear'
          },
          opacity: {
            duration: 1
          }
        }} data-id="element-77" />

          {/* Central Energy Sphere */}
          <motion.div className="relative w-28 h-28 rounded-full z-20 cursor-pointer shadow-[0_0_30px_rgba(0,0,0,0.05)]" onClick={() => {
          playSingingBowl();
          setIsPulsing(true);
          if (navigator.vibrate) navigator.vibrate([100, 50, 100]);
          setTimeout(() => setIsPulsing(false), 500);
          setTimeout(() => setShowAuraPopup(true), 300);
        }} animate={{
          scale: isPulsing ? 1.08 : 1,
          boxShadow: fillStep === 0 && baseGlow === 0 ? `0 10px 30px ${visual.glow}` : `0 0 ${visual.glowSize}px ${visual.glow}`
        }} transition={{
          scale: {
            duration: 0.4,
            type: 'spring'
          },
          boxShadow: {
            duration: 1
          }
        }} whileHover={{
          scale: 1.05
        }} data-id="element-78">
            {/* Dirty Core */}
            <motion.div className="absolute inset-0 rounded-full bg-gradient-to-tr from-slate-200 to-slate-300 blur-[2px]" animate={{
            opacity: visual.dirtyOpacity
          }} transition={{
            duration: 1
          }} data-id="element-79" />
            {/* Clean Core */}
            <motion.div className="absolute inset-0 rounded-full bg-gradient-to-tr from-emerald-400 via-cyan-400 to-indigo-500 blur-[3px]" animate={{
            opacity: visual.cleanOpacity
          }} transition={{
            duration: 1
          }} data-id="element-80" />
          </motion.div>
        </div>

        {/* Status Text */}
        <div className="mt-8 text-center relative z-20" data-id="element-81">
          <motion.p className={`text-sm font-bold ${visual.statusColor}`} animate={{
          color: fillStep > 0 ? '#059669' : '#64748b'
        }} data-id="element-82">
            {visual.status}
          </motion.p>
          <p className="text-[10px] text-slate-400 uppercase tracking-widest mt-1 font-medium" data-id="element-83">
            {visual.sub}
          </p>
        </div>
      </div>

      {/* SIMULATION PANEL */}
      <div className={`w-full clean-panel p-4 rounded-2xl mb-2 relative z-20 bg-slate-50/50 ${showSimPanel ? '' : 'hidden'}`} data-id="element-84">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 text-center" data-id="element-85">
          Panel Symulacji (Wypełnianie aury)
        </p>
        <div className="grid grid-cols-4 gap-2" data-id="element-86">
          <button onClick={e => handleFillRitual(1, '#34d399', e)} className="bg-emerald-50 border border-emerald-200 text-emerald-600 text-[9px] font-bold py-2 rounded-xl hover:bg-emerald-100 transition-colors shadow-sm" data-id="element-87">
            Rytuał Poranny
          </button>
          <button onClick={e => handleFillRitual(2, '#0ea5e9', e)} className="bg-cyan-50 border border-cyan-200 text-cyan-600 text-[9px] font-bold py-2 rounded-xl hover:bg-cyan-100 transition-colors shadow-sm" data-id="element-88">
            Rytuał Południe
          </button>
          <button onClick={e => handleFillRitual(3, '#6366f1', e)} className="bg-indigo-50 border border-indigo-200 text-indigo-600 text-[9px] font-bold py-2 rounded-xl hover:bg-indigo-100 transition-colors shadow-sm" data-id="element-89">
            Rytuał Wieczór
          </button>
          <button onClick={handleFillPoints} className="bg-yellow-50 border border-yellow-200 text-yellow-600 text-[9px] font-bold py-2 rounded-xl hover:bg-yellow-100 transition-colors shadow-sm" data-id="element-90">
            Ładuj Glow
          </button>
        </div>
        <button onClick={handleReset} className="w-full mt-2 bg-red-50 border border-red-200 text-red-500 text-[9px] font-bold py-2 rounded-xl hover:bg-red-100 transition-colors shadow-sm" data-id="element-91">
          Resetuj Aurę (Nowy Dzień)
        </button>
      </div>

      {/* Flying Particles */}
      {particles.map(p => {
      const targetX = window.innerWidth / 2;
      const targetY = orbRef.current ? orbRef.current.getBoundingClientRect().top + 128 : 300;
      return <motion.div key={p.id} className="fixed w-4 h-4 rounded-full blur-[1px] z-50 pointer-events-none" style={{
        backgroundColor: p.color,
        boxShadow: `0 0 20px ${p.color}`,
        left: 0,
        top: 0
      }} initial={{
        x: p.startX - 8,
        y: p.startY - 8,
        scale: 1,
        opacity: 1
      }} animate={{
        x: targetX - 8,
        y: targetY - 8,
        scale: 1.5,
        opacity: 0
      }} transition={{
        duration: 0.8,
        ease: [0.4, 0, 0.2, 1]
      }} data-id="element-92" />;
    })}

      {/* Aura Popup */}
      <AnimatePresence data-id="element-93">
        {showAuraPopup && <motion.div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/30 backdrop-blur-sm" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => setShowAuraPopup(false)} data-id="element-94">
            <motion.div className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-md p-6 pb-8 shadow-2xl" initial={{
          y: 100,
          opacity: 0
        }} animate={{
          y: 0,
          opacity: 1
        }} exit={{
          y: 100,
          opacity: 0
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} onClick={e => e.stopPropagation()} data-id="element-95">
              {/* Header */}
              <div className="flex items-start justify-between mb-1" data-id="element-96">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-400 via-cyan-400 to-indigo-500 flex items-center justify-center shadow-lg mb-3" data-id="element-97">
                  <Sparkles className="w-5 h-5 text-white" data-id="element-98" />
                </div>
                <button onClick={() => setShowAuraPopup(false)} className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors" data-id="element-99">
                  <X className="w-4 h-4" data-id="element-100" />
                </button>
              </div>

              <h2 className="text-xl font-bold text-zen-text mb-1" data-id="element-101">
                Zasil swoją Aurę
              </h2>
              <p className="text-sm text-slate-500 mb-6" data-id="element-102">
                Wybierz sposób na przywrócenie harmonii.
              </p>

              {/* Wykonaj Rytuał */}
              <button onClick={() => {
            playClick();
            setShowAuraPopup(false);
            onOpenScanner?.();
          }} className="w-full bg-emerald-50 border border-emerald-200 rounded-2xl p-4 flex items-center justify-between mb-3 hover:bg-emerald-100/70 transition-colors group" data-id="element-103">
                <div className="flex items-center space-x-3" data-id="element-104">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center group-hover:scale-105 transition-transform" data-id="element-105">
                    <Zap className="w-5 h-5 text-emerald-600" data-id="element-106" />
                  </div>
                  <div className="text-left" data-id="element-107">
                    <h3 className="font-bold text-sm text-zen-text" data-id="element-108">
                      Wykonaj Rytuał
                    </h3>
                    <p className="text-[11px] text-slate-500" data-id="element-109">
                      Kompleksowe oczyszczenie.
                      <br data-id="element-110" />
                      Zalecane na ten moment.
                    </p>
                  </div>
                </div>
                
              </button>

              {/* Szybka Praktyka */}
              <div className="w-full bg-slate-50 border border-slate-200 rounded-2xl p-4" data-id="element-111">
                <div className="flex items-center justify-between mb-4" data-id="element-112">
                  <div className="flex items-center space-x-3" data-id="element-113">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center" data-id="element-114">
                      <Sparkles className="w-5 h-5 text-blue-600" data-id="element-115" />
                    </div>
                    <div className="text-left" data-id="element-116">
                      <h3 className="font-bold text-sm text-zen-text" data-id="element-117">
                        Szybka Praktyka
                      </h3>
                      <p className="text-[11px] text-slate-500" data-id="element-118">
                        Wybierz jedno z narzędzi.
                        <br data-id="element-119" />
                        Zajmie tylko 2 minuty.
                      </p>
                    </div>
                  </div>
                  
                </div>

                <div className="grid grid-cols-4 gap-2" data-id="element-120">
                  <button onClick={() => {
                playClick();
                setShowAuraPopup(false);
                onOpenSoundCatalog?.();
              }} className="flex flex-col items-center space-y-1.5 bg-white rounded-xl py-3 px-2 border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-121">
                    <Music className="w-5 h-5 text-slate-500" strokeWidth={1.5} data-id="element-122" />
                    <span className="text-[10px] font-semibold text-slate-600" data-id="element-123">
                      Balans
                    </span>
                  </button>
                  <button onClick={() => {
                playClick();
                setShowAuraPopup(false);
                onOpenBeliefDetox?.();
              }} className="flex flex-col items-center space-y-1.5 bg-white rounded-xl py-3 px-2 border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-124">
                    <Brain className="w-5 h-5 text-rose-400" strokeWidth={1.5} data-id="element-125" />
                    <span className="text-[10px] font-semibold text-slate-600" data-id="element-126">
                      Detoks
                    </span>
                  </button>
                  <button onClick={() => {
                playClick();
                setShowAuraPopup(false);
                onOpenChakraCleansing?.();
              }} className="flex flex-col items-center space-y-1.5 bg-white rounded-xl py-3 px-2 border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-127">
                    <CircleDot className="w-5 h-5 text-emerald-500" strokeWidth={1.5} data-id="element-128" />
                    <span className="text-[10px] font-semibold text-slate-600" data-id="element-129">
                      Czakry
                    </span>
                  </button>
                  <button onClick={() => {
                playClick();
                setShowAuraPopup(false);
                onOpenAffirmations?.();
              }} className="flex flex-col items-center space-y-1.5 bg-white rounded-xl py-3 px-2 border border-slate-100 hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-130">
                    <Leaf className="w-5 h-5 text-green-500" strokeWidth={1.5} data-id="element-131" />
                    <span className="text-[10px] font-semibold text-slate-600" data-id="element-132">
                      Afirmacje
                    </span>
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}